// Data Loading Mixin - Prevents duplicate API calls across components
export const dataLoadingMixin = {
  data() {
    return {
      _loadingCache: null,
    }
  },
  
  created() {
    // Initialize Map in created hook to ensure it's reactive
    this._loadingCache = new Map();
  },

  methods: {
    // Cached dispatch - prevents duplicate API calls for the same action
    async $dispatchCached(action, payload = null, options = {}) {
      const { force = false, ttl = 300000 } = options; // 5 minute default TTL
      const cacheKey = `${action}_${JSON.stringify(payload)}`;
      
      // Check if we have a recent cached call
      if (!force && this._loadingCache && this._loadingCache.has(cacheKey)) {
        const cached = this._loadingCache.get(cacheKey);
        const now = Date.now();
        
        if (cached.timestamp + ttl > now) {
          
          if (cached.promise) {
            return await cached.promise;
          } else {
            return cached.result;
          }
        }
      }

      
      // Make the API call and cache the promise
      const promise = this.$store.dispatch(action, payload);
      if (this._loadingCache) {
        this._loadingCache.set(cacheKey, {
          promise,
          timestamp: Date.now()
        });
      }

      try {
        const result = await promise;
        
        // Update cache with result instead of promise
        if (this._loadingCache) {
          this._loadingCache.set(cacheKey, {
            result,
            timestamp: Date.now()
          });
        }
        
        return result;
      } catch (error) {
        // Remove failed calls from cache
        if (this._loadingCache) {
          this._loadingCache.delete(cacheKey);
        }
        throw error;
      }
    },

    // Check if data exists in store
    $hasStoreData(getter) {
      try {
        const data = this.$store.getters[getter];
        return data && (Array.isArray(data) ? data.length > 0 : true);
      } catch {
        return false;
      }
    },

    // Conditional fetch - only fetch if data doesn't exist
    async $fetchIfNeeded(action, getter, payload = null, options = {}) {
      if (options.force || !this.$hasStoreData(getter)) {
        return await this.$dispatchCached(action, payload, options);
      } else {
        return this.$store.getters[getter];
      }
    },

    // Clear cache when component is destroyed
    $clearCache() {
      if (this._loadingCache && typeof this._loadingCache.clear === 'function') {
        this._loadingCache.clear();
      }
    }
  },

  beforeDestroy() {
    this.$clearCache();
  }
};